import { Inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { HtmlLinkService } from './html-link.service';

@Injectable({
	providedIn: 'root'
})
export class SeoService {

	/**
	 * All route need to have these meta tags
	<title>PAGE NAME - WEBSITE NAME</title>
	<meta name="description" content="">
    
	<!-- for facebook -->
	<meta property="fb:app_id" content="">
    
	<meta property="og:url" content="https://WEBSITE-URL.com/">
	<meta property="og:type" content="website">
	<meta property="og:title" content="PAGE NAME - WEBSITE NAME">
	<meta property="og:image" content="https://full-path-to-image">
	<meta property="og:description" content="">
	<meta property="og:site_name" content="WEBSITE NAME"> 
    
	<!-- for twitter -->
	<meta name="twitter:card" content="summary_large_image">
	<meta name="twitter:site" content="@WEBSITE NAME IN TWITTER">
	<meta name="twitter:url" content="https://WEBSITE-URL.com/">
	<meta name="twitter:title" content="PAGE NAME - WEBSITE NAME">
	<meta name="twitter:description" content="">
	<meta name="twitter:image" content="https://full-path-to-image">
    
	<!-- Languages -->
	<link rel="canonical" href="https://WEBSITE-URL.com/lang/Your-active-page">
	<link rel="alternate" hreflang="km" href="https://WEBSITE-URL/km/..">
	<link rel="alternate" hreflang="en" href="https://WEBSITE-URL/en/..">
    
	<!-- For bookmark icon -->
	<link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png">
	<link rel="apple-touch-icon-precomposed" href="assets/img/favicon.ico">
	<meta name="msapplication-TileImage" content="assets/img/favicon.ico">
	<link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png">
	<link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png">
	<link rel="icon" href="assets/img/favicon.ico" sizes="192x192">
	<link rel="shortcut icon" href="assets/img/favicon.ico">
	 */

	baseUrl: string = '';
	defaultMetaObject: any = {
		title: "",
		description: "",
		image: "",//full url
		icon: "",
		activeLang: "en",
		activePage: "",//provide path url without lang e.g. /activity/001
		isArticle: 0,
		author: '',
		posted_at: '',
		available_langs: [{ lang_code: "en" }]
	};

	constructor(private $title: Title,
		private $meta: Meta,
		private $metaLink: HtmlLinkService, 
		@Inject('env') private $env) {
	}

	setUpMetaTags(metaObject: any = {}) {

		this.defaultMetaObject.title = this.$env.companyInfo.name;
		let newMetaObj = Object.assign(this.defaultMetaObject, metaObject);

		/** No need Remove tags before adding new tags, we will make sure our html has tags that we will update */
		let newTitle = (newMetaObj.title);
		let newImage = newMetaObj.image || this.$env.companyInfo.default_image;
		let fullActiveUrl: string = `${this.$env.companyInfo.url}/${newMetaObj.activeLang}${newMetaObj.activePage}`;

		this.setupBrowserMeta(newTitle);
		this.setupGeneralMeta(newTitle, newMetaObj.description, newImage);
		this.setupFacebookMeta(newTitle, newMetaObj.description, newImage, fullActiveUrl, newMetaObj.isArticle);
		this.setupTwitterMeta(newTitle, newMetaObj.description, newImage, fullActiveUrl);
		// this.setupIconsMeta();
		this.setupLanguagesMeta(metaObject);

	}

	setupBrowserMeta(title: string = 'Home') {
		this.$title.setTitle(title);
	}

	setupGeneralMeta(title: string = 'Home', description: string, image: string) {

		this.$meta.updateTag({ name: 'description', content: description }, );
		this.$meta.updateTag({ name: 'image', content: image });
		this.$meta.updateTag({ name: 'msapplication-TileImage', content: image });
		this.$meta.updateTag({ name: 'application-name', content: title });
		this.$meta.updateTag({ itemprop: 'name', content: title }, 'itemprop="name"');
		this.$meta.updateTag({ itemprop: 'description', content: description}, 'itemprop="description"');
		this.$meta.updateTag({ itemprop: 'image', content: image }, 'itemprop="image"');
		this.$meta.updateTag({ name: 'apple-mobile-web-app-title', content: title });
	}

	setupFacebookMeta(title: string = 'Home', description: string, image: string, fullActiveUrl: string, isArticle: boolean = false) {
		if (this.$env.companyInfo.fbid) {
			this.$meta.updateTag({ property: 'fb:app_id', content: this.$env.companyInfo.fbid });
		}

		this.$meta.updateTag({ property: 'og:title', content: title });
		this.$meta.updateTag({ property: 'og:description', content: description });
		this.$meta.updateTag({ property: 'og:type', content: (isArticle ? 'article' : 'website') });
		this.$meta.updateTag({ property: 'og:url', content: fullActiveUrl });
		this.$meta.updateTag({ property: 'og:image', content: image });
		this.$meta.updateTag({ property: 'og:site_name', content: this.$env.companyInfo.name });

		if (isArticle) {
			let imageWidth: string = this.$env.articleMeta.image_width || "780";
			let imageHeight: string = this.$env.articleMeta.image_height || "440";
			this.$meta.updateTag({ property: 'og:image:width', content: imageWidth });
			this.$meta.updateTag({ property: 'og:image:height', content: imageHeight });
		}
	}

	setupTwitterMeta(title: string = 'Home', description: string, image: string, fullActiveUrl: string) {
		this.$meta.updateTag({ name: 'twitter:url', content: fullActiveUrl });
		this.$meta.updateTag({ name: 'twitter:title', content: title });
		this.$meta.updateTag({ name: 'twitter:description', content: description });
		this.$meta.updateTag({ name: 'twitter:card', content: this.$env.articleMeta.twitter_card });
		this.$meta.updateTag({ name: 'twitter:site', content: this.$env.companyInfo.twitter_name });
		this.$meta.updateTag({ name: 'twitter:image', content: image });
		this.$meta.updateTag({ name: 'twitter:creator', content: this.$env.companyInfo.name });
	}

	setupIconsMeta() {
		this.$metaLink.updateTag({ rel: 'icon', href: this.$env.companyInfo.icon, sizes: '16x16' });
		this.$metaLink.updateTag({ rel: 'icon', href: this.$env.companyInfo.icon, sizes: '32x32' });
		this.$metaLink.updateTag({ rel: 'icon', href: this.$env.companyInfo.icon, sizes: '192x192' });
		this.$metaLink.updateTag({ rel: 'shortcut icon', href: this.$env.companyInfo.icon });
		this.$metaLink.updateTag({ rel: 'icon', type: 'image/x-icon', href: this.$env.companyInfo.icon });

		this.$metaLink.updateTag({ rel: 'apple-touch-icon', href: this.$env.companyInfo.icon });
		this.$metaLink.updateTag({ rel: 'apple-touch-icon-precomposed', href: this.$env.companyInfo.icon });
		this.$metaLink.updateTag({ rel: 'apple-touch-icon-precomposed', href: this.$env.companyInfo.icon });
	}

	// // /** Language alternate link */
	setupLanguagesMeta(metaObject: any) { 
		

		this.$metaLink.updateTag({ rel: 'canonical', href: this.$env.companyInfo.url + '/' + metaObject.activeLang + metaObject.activePage });
		if(metaObject.available_langs && metaObject.available_langs.length){
			// remove rel:alternate for language
			metaObject.available_langs.forEach(lang => {
				this.$metaLink.removeTag({rel:"alternate", hreflang: lang.lang_code});
			});

			metaObject.available_langs.forEach(lang => {
				if(lang.lang_code != metaObject.activeLang){
					this.$metaLink.addTag({ rel: 'alternate', hreflang: lang.lang_code, href: this.$env.companyInfo.url + '/' + lang.lang_code + metaObject.activePage });
				}
			});
		}
		
	}

 
}
